import {safeNumber} from "../api/safeNumber";
import {instantText} from "../account/accountMessages";
import type {OperatorBooking} from "../gen/kitecorp/transferify/admin/v1/sponsor_admin_pb";
import {OperatorBooking_State} from "../gen/kitecorp/transferify/admin/v1/sponsor_admin_pb";
import {BackdropCategory, BackdropTemplate} from "../gen/kitecorp/transferify/storefront/v1/backdrop_pb";
import {sponsorOptionsOf, takeoverTemplateKeyOf} from "./backdropOptionsMessages";
import type {SponsorAdminBooking, SponsorBookingState} from "./SponsorApi";
import type {SponsorCategory} from "./sponsorBooking";

/**
 * The operator's review row (`OperatorBooking`, kitecorp/transferify-ui#4024) as the queue and the schedule read it.
 *
 * A treatment or a state this bundle cannot name is KEPT, not dropped, because the operator still has to review what
 * was paid for: the template is the contract's name for it, or its number in text, which the page shows as it came
 * (`AdminSponsorsPage` falls back to the wire string for a name outside its catalogue), and the state is
 * `"UNSPECIFIED"`, which `isReviewable` offers no button on. A category left unspecified is ABSENT, as the row was
 * before the field existed. Amounts and counts are `int64`: a figure a number cannot hold exactly throws rather than
 * showing a rounded sum.
 */
export function sponsorAdminBookingOf(message: OperatorBooking): SponsorAdminBooking {
	const category = BackdropCategory[message.category] as SponsorCategory | "UNSPECIFIED" | undefined;
	return {
		bookingId: message.bookingId,
		brandKey: message.brandKey,
		// The union has no member for a state outside the contract; the page renders one as an empty chip.
		state: (OperatorBooking_State[message.state] ?? "UNSPECIFIED") as SponsorBookingState,
		template: (takeoverTemplateKeyOf(message.template)
			?? BackdropTemplate[message.template] ?? String(message.template)) as SponsorAdminBooking["template"],
		...(category === undefined || category === "UNSPECIFIED" ? {} : {category}),
		options: sponsorOptionsOf(message.options),
		logoUrl: message.logoUrl ?? null,
		sponsorName: message.sponsorName,
		sponsorEmail: message.sponsorEmail,
		headline: message.headline,
		tagline: message.tagline ?? null,
		ctaLabel: message.ctaLabel,
		ctaUrl: message.ctaUrl,
		startsOn: message.startsOn,
		endsOn: message.endsOn,
		amountMinor: safeNumber(message.amountMinor),
		currency: message.currency,
		impressions: safeNumber(message.impressions),
		rejectionReason: message.rejectionReason ?? null,
		paymentDueAt: message.paymentDueAt === undefined ? null : instantText(message.paymentDueAt),
		refundDueAt: message.refundDueAt === undefined ? null : instantText(message.refundDueAt),
		imageUrls: [...message.imageUrls],
		videoUrl: message.videoUrl ?? null,
		posterUrl: message.posterUrl ?? null,
	};
}
